import clsx from 'clsx';
import * as React from 'react';
import { type ContextValue, useContextProps } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';

import { Button } from '../Button';

export type PaginationRangeProps = {
    /** Label for the range of items being displayed */
    label?: string;
    /** Total number of items in the list */
    totalItems: number;
    /** Number of items displayed per page */
    itemsPerPage: number;
    /** Index of the current page */
    page: number;
};

export type PaginationNavigationCallbacks = {
    /** Function to call when the user clicks the Next button. */
    next: (() => void | Promise<void>) | undefined;
    /** Function to call when the user clicks the Previous button. */
    previous: (() => void | Promise<void>) | undefined;
};

export type PaginationNavigationProps = PaginationNavigationCallbacks & {
    className?: string;
};

export type PaginationProps = PaginationNavigationProps & {
    /**
     * Info about the range of items being displayed
     */
    range?: PaginationRangeProps;
};

export const PaginationContext = React.createContext<
    ContextValue<Partial<PaginationProps>, HTMLDivElement>
>({});

/**
 * Footer for a paginated list to display Next/Previous buttons.
 */
export const Pagination = React.forwardRef(function Pagination(
    props: PaginationProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    [props, ref] = useContextProps(props, ref, PaginationContext);
    const { className, range, ...actionProps } = props;

    if (!range) {
        return <PaginationNavigation ref={ref} {...actionProps} className={className} />;
    }

    if (range.totalItems <= 1 && !actionProps.next && !actionProps.previous) {
        return null;
    }

    return (
        <div ref={ref} className={clsx('flex items-center justify-between gap-2', className)}>
            {range.totalItems > 1 ? <PaginationRange {...range} /> : null}
            <PaginationNavigation {...actionProps} />
        </div>
    );
});

const PaginationNavigation = React.forwardRef(function PaginationNavigation(
    props: PaginationNavigationProps,
    ref: React.ForwardedRef<HTMLDivElement>
): React.ReactElement | null {
    const { className, next, previous } = props;

    return next || previous ? (
        <nav
            ref={ref}
            aria-label="Pagination Navigation"
            className={clsx('flex justify-end gap-2', className)}
        >
            {previous ? (
                <Button
                    leadingIcon={Icon.ChevronLeft}
                    kind="blank"
                    size="small"
                    onPress={async () => {
                        await previous();
                    }}
                    label="Previous"
                />
            ) : null}
            {next ? (
                <Button
                    trailingIcon={Icon.ChevronRight}
                    kind="blank"
                    size="small"
                    onPress={async () => {
                        await next();
                    }}
                    label="Next"
                />
            ) : null}
        </nav>
    ) : null;
});

function PaginationRange(props: PaginationRangeProps): React.ReactElement {
    const { totalItems, itemsPerPage, page, label = 'items' } = props;

    const start = page * itemsPerPage + 1;
    const end = Math.min((page + 1) * itemsPerPage, totalItems);

    return (
        <div aria-live="polite" className="text-muted text-ui-small">
            {start} - {end} of {totalItems} {label}
        </div>
    );
}
